import React, { useEffect, useMemo, useState } from "react";

import SectionCard from "../ui/SectionCard";
import Button from "../../../components/Button/Button.jsx";

import { getAllEvents } from "../../../services/eventServices";

import {
  FaCalendarAlt,
  FaEdit,
  FaListUl,
  FaSearch,
  FaSyncAlt,
} from "react-icons/fa";

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const isValidDate = (value) => {
  if (!value) return false;

  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const toDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const formatDateFr = (value) => {
  if (!isValidDate(value)) return "—";

  return new Date(value).toLocaleDateString("fr-FR", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const DEFAULT_TYPE_EVENT = "Compétition";
const DEFAULT_ORGANISATEUR = "Ligue NA";

export default function EventSelectCard({
  modeValue = "calendar",
  value,
  titreValue = "",
  startValue = "",
  lieuValue = "",
  onChange,
  initialType = DEFAULT_TYPE_EVENT,
  initialOrganisateur = DEFAULT_ORGANISATEUR,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");

  const [typeEvent, setTypeEvent] = useState(initialType || DEFAULT_TYPE_EVENT);

  const [organisateur, setOrganisateur] = useState(
    initialOrganisateur || DEFAULT_ORGANISATEUR,
  );

  const mode = modeValue === "manual" ? "manual" : "calendar";

  const fetchEvents = async () => {
    try {
      setError("");
      setLoading(true);

      const data = await getAllEvents();

      const visibleEvents = Array.isArray(data)
        ? data.filter((event) => {
            /*
             * On masque uniquement les événements explicitement inactifs.
             * Si is_active n'existe pas dans la réponse, l'événement reste visible.
             */
            if (event?.is_active === undefined || event?.is_active === null) {
              return true;
            }

            return (
              event.is_active === true ||
              event.is_active === 1 ||
              event.is_active === "1"
            );
          })
        : [];

      setEvents(visibleEvents);
    } catch (fetchError) {
      console.error(fetchError);
      setError("Impossible de charger la liste des événements.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const allTypes = useMemo(() => {
    return Array.from(
      new Set(events.map((event) => event.type_event).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  const allOrganisateurs = useMemo(() => {
    return Array.from(
      new Set(events.map((event) => event.organisateur).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  const filtered = useMemo(() => {
    let result = [...events];

    if (typeEvent) {
      result = result.filter((event) => event.type_event === typeEvent);
    }

    if (organisateur) {
      result = result.filter((event) => event.organisateur === organisateur);
    }

    if (q.trim()) {
      const needle = normalize(q);

      result = result.filter((event) =>
        [
          event.titre,
          event.organisateur,
          event.type_event,
          event.level_event,
          event.lieu_event,
          event.description,
        ]
          .map(normalize)
          .join(" ")
          .includes(needle),
      );
    }

    result.sort((a, b) => {
      const dateA = isValidDate(a.start) ? new Date(a.start).getTime() : 0;

      const dateB = isValidDate(b.start) ? new Date(b.start).getTime() : 0;

      return dateA - dateB;
    });

    return result;
  }, [events, q, typeEvent, organisateur]);

  const selected = useMemo(() => {
    if (!value) return null;

    return events.find((event) => Number(event.id) === Number(value)) || null;
  }, [events, value]);

  const manualComplete =
    !!String(titreValue || "").trim() &&
    !!startValue &&
    !!String(lieuValue || "").trim();

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;

    onChange?.({
      mode: nextMode,
      event_id: null,
      titre: "",
      start: null,
      lieu_event: "",
      resetClaim: true,
    });
  };

  const handlePick = (eventId) => {
    const event = events.find((row) => Number(row.id) === Number(eventId));

    if (!event) return;

    onChange?.({
      mode: "calendar",
      event_id: event.id,
      titre: event.titre || "",
      start: toDateInputValue(event.start),
      lieu_event: event.lieu_event || "",
      resetClaim: true,
    });
  };

  const updateManualEvent = (field, fieldValue) => {
    onChange?.({
      mode: "manual",
      event_id: null,

      titre: field === "titre" ? fieldValue : titreValue,

      start: field === "start" ? fieldValue || null : startValue || null,

      lieu_event: field === "lieu_event" ? fieldValue : lieuValue,

      resetClaim: false,
    });
  };

  const resetFilters = () => {
    setQ("");
    setTypeEvent(initialType || DEFAULT_TYPE_EVENT);
    setOrganisateur(initialOrganisateur || DEFAULT_ORGANISATEUR);
  };

  return (
    <SectionCard
      id="fiche"
      title="Réunion ou événement"
      subtitle="Sélectionnez un événement du calendrier ou renseignez manuellement une réunion non présente dans le calendrier sportif."
      icon={<FaCalendarAlt />}
      actions={
        mode === "calendar" ? (
          <div className="flex flex-col gap-2">
            <Button
              text={loading ? "Chargement..." : "Rafraîchir"}
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              icon={FaSyncAlt}
            />

            <Button
              text="Réinitialiser les filtres"
              variant="outline"
              size="sm"
              onClick={resetFilters}
            />
          </div>
        ) : null
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => switchMode("calendar")}
          className={[
            "flex items-center gap-3 rounded-2xl p-4 text-left ring-1 transition",
            mode === "calendar"
              ? "bg-indigo-50 text-indigo-900 ring-indigo-300"
              : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
          ].join(" ")}
        >
          <span
            className={[
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              mode === "calendar"
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 text-slate-600",
            ].join(" ")}
          >
            <FaListUl />
          </span>

          <span>
            <span className="block font-bold">Événement du calendrier</span>

            <span className="mt-1 block text-xs opacity-75">
              Compétition, stage ou événement déjà enregistré.
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => switchMode("manual")}
          className={[
            "flex items-center gap-3 rounded-2xl p-4 text-left ring-1 transition",
            mode === "manual"
              ? "bg-amber-50 text-amber-900 ring-amber-300"
              : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
          ].join(" ")}
        >
          <span
            className={[
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              mode === "manual"
                ? "bg-amber-500 text-white"
                : "bg-slate-100 text-slate-600",
            ].join(" ")}
          >
            <FaEdit />
          </span>

          <span>
            <span className="block font-bold">Saisie manuelle</span>

            <span className="mt-1 block text-xs opacity-75">
              Réunion de comité ou déplacement hors calendrier.
            </span>
          </span>
        </button>
      </div>

      {mode === "manual" ? (
        <div className="mt-4 rounded-2xl bg-amber-50/60 p-4 ring-1 ring-amber-200">
          <div className="flex items-center gap-2 font-bold text-amber-950">
            <FaEdit />
            Informations saisies manuellement
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <div className="mb-1 text-sm font-semibold text-slate-700">
                Nom de la réunion ou de l’événement *
              </div>

              <input
                type="text"
                value={titreValue}
                onChange={(event) =>
                  updateManualEvent("titre", event.target.value)
                }
                placeholder="Ex : Réunion du comité départemental de la Gironde"
                className="w-full rounded-xl bg-white px-3 py-2.5 text-slate-900 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </label>

            <label className="block">
              <div className="mb-1 text-sm font-semibold text-slate-700">
                Date *
              </div>

              <input
                type="date"
                value={toDateInputValue(startValue)}
                onChange={(event) =>
                  updateManualEvent("start", event.target.value)
                }
                className="w-full rounded-xl bg-white px-3 py-2.5 text-slate-900 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </label>

            <label className="block">
              <div className="mb-1 text-sm font-semibold text-slate-700">
                Lieu *
              </div>

              <input
                type="text"
                value={lieuValue}
                onChange={(event) =>
                  updateManualEvent("lieu_event", event.target.value)
                }
                placeholder="Ex : Maison des Sports, Bordeaux"
                className="w-full rounded-xl bg-white px-3 py-2.5 text-slate-900 ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </label>
          </div>

          <div
            className={[
              "mt-4 rounded-xl p-3 text-sm ring-1",
              manualComplete
                ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                : "bg-white text-slate-600 ring-slate-200",
            ].join(" ")}
          >
            {manualComplete ? (
              <>
                <div className="font-bold">Informations complètes</div>

                <div className="mt-1">
                  {titreValue} • {formatDateFr(startValue)} • {lieuValue}
                </div>
              </>
            ) : (
              "Le nom, la date et le lieu sont obligatoires."
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-xl bg-white p-4 ring-1 ring-slate-200">
            <div className="text-sm font-semibold text-slate-800">
              Événement sélectionné
            </div>

            {selected ? (
              <div className="mt-2 text-sm text-slate-700">
                <div className="font-semibold">{selected.titre || "—"}</div>

                <div className="opacity-80">
                  {formatDateFr(selected.start)} • {selected.lieu_event || "—"}
                </div>

                <div className="mt-1 text-xs text-slate-500">
                  {selected.type_event || "—"}

                  {selected.level_event ? ` • ${selected.level_event}` : ""}
                </div>
              </div>
            ) : (
              <div className="mt-2 text-sm text-slate-500">
                Aucun événement sélectionné.
              </div>
            )}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-12">
            <div className="md:col-span-6">
              <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-slate-200">
                <FaSearch className="text-slate-500" />

                <input
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder="Rechercher un titre, un lieu..."
                  className="w-full bg-transparent text-slate-900 outline-none"
                />
              </div>
            </div>

            <select
              value={typeEvent}
              onChange={(event) => setTypeEvent(event.target.value)}
              className="rounded-xl bg-white px-3 py-2 text-slate-900 ring-1 ring-slate-200 md:col-span-3"
            >
              <option value="">Type : tous</option>

              {allTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              value={organisateur}
              onChange={(event) => setOrganisateur(event.target.value)}
              className="rounded-xl bg-white px-3 py-2 text-slate-900 ring-1 ring-slate-200 md:col-span-3"
            >
              <option value="">Organisateur : tous</option>

              {allOrganisateurs.map((row) => (
                <option key={row} value={row}>
                  {row}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
            {error ? (
              <div className="bg-rose-50 p-4 text-rose-800">{error}</div>
            ) : null}

            {loading ? (
              <div className="p-4 text-sm text-slate-600">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-slate-600">
                Aucun événement trouvé.
              </div>
            ) : (
              <div className="max-h-[380px] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-slate-700">
                    <tr className="text-left">
                      <th className="px-3 py-2 text-right">Choisir</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Titre</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Lieu</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((event) => {
                      const active = Number(event.id) === Number(value);

                      return (
                        <tr
                          key={event.id}
                          className={
                            active ? "bg-emerald-50" : "hover:bg-slate-50"
                          }
                        >
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handlePick(event.id)}
                              className={[
                                "h-9 rounded-xl px-3 text-sm font-semibold ring-1 transition",
                                active
                                  ? "bg-emerald-600 text-white ring-emerald-700 hover:bg-emerald-700"
                                  : "bg-white text-slate-800 ring-slate-200 hover:bg-slate-50",
                              ].join(" ")}
                            >
                              {active ? "Sélectionné" : "Choisir"}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                            {formatDateFr(event.start)}
                          </td>

                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-900">
                              {event.titre || "—"}
                            </div>

                            <div className="text-xs text-slate-500">
                              {event.organisateur || "—"}
                            </div>
                          </td>

                          <td className="px-3 py-2 text-slate-700">
                            {event.type_event || "—"}
                          </td>

                          <td className="px-3 py-2 text-slate-700">
                            {event.lieu_event || "—"}
                          </td>

                          
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </SectionCard>
  );
}
